import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import { DataGrid, GridColDef, GridAlignment } from '@mui/x-data-grid';
import { ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { atom, useSetAtom } from 'jotai';
import { useStoreOwnerparams } from './useStoreOwnerparams';
import AddStoreOwner from '../AddStoreOwner';
import EditStoreOwner from '../EditStoreOwner';
import EditStoreOwnerModal from '../EditStoreOwnerModal';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../../context/PermissionContext';
import { COLORS } from '../../../theme/themeConstants';
import { theme } from '../../../theme';
import {
  useGetAllStoreOwnersQuery,
  useDeleteStoreOwnerByIdQuery,
  useToggleStoreOwnerStatusQuery,
} from '../../Query/AddStoreQuery';
import { MerchantSchema } from '../../../client-sdk';
import { useDebounce } from '../Utils/useDounce';
import { categoryStyles } from '../../Styles/CategoryFormStyles';
import { useSnackbar } from '../../Stateless/SnackBarProvider';

type StoreOwnerWithSno = Partial<MerchantSchema> & {
  id: string;
  sno: number;
};

export const storeOwnerAtom = atom<MerchantSchema | null>(null);

export default function StoreOwnerTable() {
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [modalContent, setModalContent] = useState<'add' | 'edit'>('edit');
  const deleteStoreOwnerMutation = useDeleteStoreOwnerByIdQuery();
  const toggleStoreOwnerStatusMutation = useToggleStoreOwnerStatusQuery();
  const setStoreOwnerAtom = useSetAtom(storeOwnerAtom);
  const [selectedStoreOwner, setSelectedStoreOwner] =
    React.useState<MerchantSchema | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useStoreOwnerparams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = React.useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const { data: storeOwnersResponse, isLoading } = useGetAllStoreOwnersQuery(memoizedParams);

  const filteredRows = React.useMemo(() => {
    const storeOwners = storeOwnersResponse?.data || [];
    const page = params.page || 1;
    const limit = params.limit || 10;

    // Add sno field to each row and ensure all required fields are present
    return storeOwners.map((row, index) => {
      // Ensure we have a valid row object
      if (!row)
        return { id: `empty-${index}`, sno: index + 1 } as StoreOwnerWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.id || `empty-${index}`,
        // sno: index + 1,
        sno: (page - 1) * limit + index + 1,
      };

      return rowWithSno as StoreOwnerWithSno;
    });
  }, [params.search, storeOwnersResponse, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const handleOpenEditModal = (storeOwner: MerchantSchema) => {
    setSelectedStoreOwner(storeOwner);
    setModalContent('edit');
    setOpenEditDialog(true);
    setStoreOwnerAtom(storeOwner);
  };

  const handleOpenAddModal = () => {
    setModalContent('add');
    setOpenAddDialog(true);
    setSelectedStoreOwner(null);
    setStoreOwnerAtom(null);
  };

  const handleCloseModal = () => {
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedStoreOwner(null);
    setStoreOwnerAtom(null);
  };

  const handleBlock = (ids: string[]) => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one store owner to block', 'error');
      return;
    }
    if (
      window.confirm('Are you sure you want to block the selected store owner?')
    ) {
      ids.forEach((id) => {
        toggleStoreOwnerStatusMutation.mutate({ id, status: 'inactive' });
      });
    }
  };

  const handleUnblock = (ids: string[]) => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one store owner to unblock', 'error');
      return;
    }
    if (
      window.confirm(
        'Are you sure you want to unblock the selected store owner?'
      )
    ) {
      ids.forEach((id) => {
        toggleStoreOwnerStatusMutation.mutate({ id, status: 'active' });
      });
    }
  };

  const handleDelete = (ids: string[]) => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one store owner to delete', 'error');
      return;
    }
    if (
      window.confirm(
        'Are you sure you want to delete the selected store owner? This action cannot be undone.'
      )
    ) {
      ids.forEach((id) => {
        deleteStoreOwnerMutation.mutate(id);
      });
      setSelectedRows([])
    }
  };

  const handleBlockClick = (ids: string[]) => {
  if (window.confirm('Are you sure you want to block this store owner?')) {
    ids.forEach((id) => {
      toggleStoreOwnerStatusMutation.mutate({ id, status: 'inactive' });
    });
  }
};

  // Compute which bulk actions to show based on selected rows' current status
  const { showBlock, showUnblock } = React.useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = storeOwnersResponse?.data || [];
    const idToActive = new Map<string, boolean>(
      dataList.map((item: any) => [String(item.id), Boolean(item.isActive)])
    );
    const statuses = selectedRows.map((id) => idToActive.get(String(id)));
    const hasActive = statuses.some((s) => s === true);
    const hasInactive = statuses.some((s) => s === false);
    return {
      showBlock: hasActive,     // show Block when any selected is active
      showUnblock: hasInactive, // show Unblock when any selected is inactive
    };
  }, [selectedRows, storeOwnersResponse]);

const handleUnblockClick = (ids: string[]) => {
  if (window.confirm('Are you sure you want to unblock this store owner?')) {
    ids.forEach((id) => {
      toggleStoreOwnerStatusMutation.mutate({ id, status: 'active' });
    });
  }
};

const handleDeleteClick = (ids: string[]) => {
  if (window.confirm('Are you sure you want to delete this store owner? This action cannot be undone.')) {
    ids.forEach((id) => {
      deleteStoreOwnerMutation.mutate(id);
    });
  }
};

  const columns: GridColDef<StoreOwnerWithSno>[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      width: 80,
      renderCell: (params) => {
        // const index = filteredRows.findIndex((row) => row.id === params.row.id);
        return (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontWeight: 500 }}
          >
            {params.row.sno}
          </Typography>
        );
      },
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      valueGetter: (params: { row: MerchantSchema }) => {
        if (!params) return '';
        return `${params || ''}`;
      },
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1,
      valueGetter: (params: { row: MerchantSchema }) => params || '',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'businessType',
      headerName: 'Business Type',
      flex: 1,
      valueGetter: (params: { row: MerchantSchema }) => params || '',
      renderCell: (params) => (
        <Box
          sx={{
            px: 1.5,
            // py: 0.5,
            borderRadius: 1.5,
            bgcolor: 'primary.50',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      valueGetter: (params: { row: MerchantSchema }) => params || '',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'mobileNo',
      headerName: 'Mobile',
      flex: 1,
      valueGetter: (params: { row: MerchantSchema }) => params || '',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value}
        </Typography>
      ),
    },
    ...((hasPermission("storeOwner", 'Block') || hasPermission("storeOwner", 'Unblock')) ? [
   {
      field: 'block',
      headerName: 'Status',
      width: 120,
      sortable: false,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: MerchantSchema }) => {
        if (!params?.row) return null;
        const isActive = params.row.isActive;
        const canBlock = hasPermission('storeOwner', 'block');
        const canUnblock = hasPermission('storeOwner', 'UnBlock');
        if ((isActive && !canBlock) || (!isActive && !canUnblock)) return null;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: isActive ? 'success.50' : 'error.50',
              color: isActive ? 'success.main' : 'error.main',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: isActive ? 'success.100' : 'error.100',
              },
            }}
            onClick={() => {
              const isActive = params.row.isActive;
              const storeOwnerId = params.row.id || '';

              if (isActive) {
                handleBlockClick([storeOwnerId]);
              } else {
                handleUnblockClick([storeOwnerId]);
              }
            }}
          >
            {isActive ? (
              <CheckCircleIcon fontSize="small" />
            ) : (
              <BlockIcon fontSize="small" />
            )}
          </Box>
        );
      },
    }
  ] : []),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: { row: MerchantSchema }) => {
        if (!params?.row) return null;
        const canEdit = hasPermission('storeOwner', 'Edit');
        const canDelete = hasPermission('storeOwner', 'Delete');
        if (!canEdit && !canDelete) return null;
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);
        const id = params.row.id || '';
        return (
          <>
            <IconButton 
              size="small" 
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.2rem',
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {canEdit && (
                <MenuItem onClick={() => { setAnchorEl(null); handleOpenEditModal(params.row); }}>
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem onClick={() => { setAnchorEl(null); handleDeleteClick([id]); }}>
                  <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={categoryStyles.ListContainer}>
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 600,
                border: '1px solid #ccc',
                borderRadius: '8px',
                background: '#fff',
              }}
            >
              <TextField
                fullWidth
                placeholder="Search by name, email"
                size="medium"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{
                  '.MuiOutlinedInput-root': {
                    height: 40,
                    width: 600,
                    border: 'none', // remove inner border
                  },
                  '& fieldset': {
                    border: 'none', // remove Material-UI default border
                  },
                  '& .MuiInputBase-input': {
                    paddingTop: '10px', // adjust this value to move the placeholder lower
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                '& .MuiButton-root': {
                  minWidth: 'auto',
                  px: 2,
                },
              }}
            >
              <PermissionActionButtons
                module="storeOwner"
                onBlock={showBlock ? () => handleBlock(selectedRows) : undefined}
                onUnblock={showUnblock ? () => handleUnblock(selectedRows) : undefined}
                onDelete={selectedRows.length ? () => handleDelete(selectedRows) : undefined}
              />
              {hasPermission('storeOwner', 'Add') && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={handleOpenAddModal}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    bgcolor: COLORS.primary.main,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: COLORS.primary.main,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  Add Store Owner
                </Button>
              )}
            </Stack>
          </Box>
        </Box>

        {/* <Box sx={{  width: '100%' }}> */}
        <DataGrid<StoreOwnerWithSno>
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          autoHeight
          rows={filteredRows || []}
          columns={columns}
          getRowId={(row) => row.id || ''}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: params.limit,
                page: (params.page || 1) - 1,
              },
            },
          }}
          rowCount={storeOwnersResponse?.pagination?.totalItems || 0}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
          // sortingMode="server"
          onPaginationModelChange={({ pageSize: newSize, page: newPage }) => {
            handlePageSizeChange(newSize);
            handlePageChange?.(newPage);
          }}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection as string[]);
          }}
          rowSelectionModel={selectedRows}
          filterMode="client"
          loading={isLoading}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            '& .MuiDataGrid-main': {
              borderRadius: 2,
              overflow: 'hidden',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'grey.50',
              fontWeight: 700,
              fontSize: '0.875rem',
              color: 'text.primary',
              borderBottom: '2px solid',
              borderColor: 'divider',
              borderRadius: 0,
              '& .MuiDataGrid-columnHeader': {
                '&:focus, &:focus-within': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: 'text.primary',
              },
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'divider',
              padding: 2,
              '&:focus, &:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'grey.50',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.50',
                '&:hover': {
                  backgroundColor: 'primary.100',
                },
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50',
            },
            '& .MuiDataGrid-selectedRowCount': {
              color: 'text.primary',
              fontWeight: 500,
            },
            '& .MuiDataGrid-virtualScroller': {
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '6px',
                height: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#888',
                borderRadius: '3px',
                '&:hover': {
                  background: '#666',
                },
              },
            },
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'linear-progress',
            },
          }}
        />
        {/* </Box> */}
        <EditStoreOwnerModal
          open={openEditDialog || openAddDialog}
          onClose={handleCloseModal}
          title={
            modalContent === 'add' ? 'Add Store Owner' : 'Edit Store Owner'
          }
        >
          {modalContent === 'add' ? (
            <AddStoreOwner
              onClose={handleCloseModal}
              storeOwner={selectedStoreOwner}
            />
          ) : (
            selectedStoreOwner && (
              <EditStoreOwner
                onClose={handleCloseModal}
                storeOwner={selectedStoreOwner}
              />
            )
          )}
        </EditStoreOwnerModal>
      </Box>
    </ThemeProvider>
  );
}
